import { useRouter } from 'next/router'
import styled, { useTheme } from 'styled-components'
import { ImageAsset } from '@/components/common'
import { InputCheckbox } from '@/components/common/inputs'
import { SkeletonLine } from '@/components/domains/category/SkeletonLine'
import { GlobalSizeEnum } from '@/constants/global-size.enum'
import { SCREEN_MEDIA_QUERY } from '@/constants/legacy/constLayout'
import { PRODUCTS_SELECT_FILTER_ORDER_BY_V4 } from '@/constants/products-select-filter-orderby.const'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { QueryStringValueEnum } from '@/constants/query-string-value.enum'
import { SearchResultContainer } from '@/containers/search/search-result.context'
import { Typo, TypoVariant } from '../typography'

const productFilterTopBarOptions = [
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.PRICE_ASC,
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.BEST_SELLING,
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.REVIEW_SCORE,
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.REVIEW_COUNT,
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.DISCOUNT_RATE,
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.RECOMMEND,
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.PRICE_DESC,
  PRODUCTS_SELECT_FILTER_ORDER_BY_V4.NEWEST,
]

const ProductFilterTopBarForV4 = () => {
  const {
    pathname,
    query: {
      [QueryStringKeyEnum.OrderBy]: sortBy,
      [QueryStringKeyEnum.FilterExcept]: except,
      [QueryStringKeyEnum.SearchQuery]: searchKeywordQueryParam,
      keyword: searchKeywordPathParam,
    },
  } = useRouter()

  const { appendFilterQuery, totalCount, isLoading } = SearchResultContainer.useContainer()
  const { color } = useTheme()
  const searchKeyword = searchKeywordQueryParam || searchKeywordPathParam || ''

  const handleChangeSoldOut = (e: React.ChangeEvent<HTMLInputElement>) => {
    appendFilterQuery(pathname, {
      [QueryStringKeyEnum.FilterExcept]: e.target.checked ? QueryStringValueEnum.SoldOut : QueryStringValueEnum.None,
    })
  }

  const handleSelectFilterOption = (e: React.ChangeEvent<HTMLSelectElement>) => {
    appendFilterQuery(pathname, { [QueryStringKeyEnum.OrderBy]: e.target.value })
  }

  return (
    <StyledProductFilterTopBarContainer>
      <div className="product-filter-top-bar__bread-crumb">
        <Typo as="h1" variant={TypoVariant.Body5Regular} color={color.gray200}>
          {searchKeyword} | 검색 결과 - 핏펫몰
        </Typo>
      </div>
      <div className="product-filter-top-bar__container">
        <div className="product-filter-top-bar__container-left">
          {!isLoading ? (
            <Typo variant={TypoVariant.Body3Regular} color={color.gray500}>
              {totalCount?.toLocaleString()}개
            </Typo>
          ) : (
            <SkeletonLine height="1.8rem" width="2.4rem" />
          )}

          <InputCheckbox
            id="soldout"
            checked={except === QueryStringValueEnum.SoldOut}
            onChange={handleChangeSoldOut}
            size={GlobalSizeEnum.Sm}
          >
            <Typo variant={TypoVariant.Body3Regular} color={color.gray500}>
              품절상품 제외
            </Typo>
          </InputCheckbox>
        </div>
        <div className="product-filter-top-bar__container-right">
          <select
            className="product-filter-top-bar__select-filter"
            value={sortBy || PRODUCTS_SELECT_FILTER_ORDER_BY_V4.RECOMMEND.value}
            onChange={handleSelectFilterOption}
          >
            {productFilterTopBarOptions.map(({ value, text }) => (
              <Typo as="option" key={value} value={value} variant={TypoVariant.Body3Regular} color={color.gray500}>
                {text}
              </Typo>
            ))}
          </select>
          <ImageAsset name="ic_down_gray700_w6" svg />
        </div>
      </div>
    </StyledProductFilterTopBarContainer>
  )
}

const StyledProductFilterTopBarContainer = styled.div`
  padding: 0 1.6rem;
  position: relative;
  background-color: ${({
    theme: {
      color: { grayWhite },
    },
  }) => grayWhite};
  .product-filter-top-bar {
    &__bread-crumb {
      margin: 0.4rem 0 0.8rem;
    }
    &__container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1.4rem;
    }
    &__container-left {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1.2rem;
    }
    &__container-right {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
    }
    &__select-filter {
      border: none;
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      font-size: 16px;
      @media ${SCREEN_MEDIA_QUERY.xs} {
        font-size: 14px;
      }
      color: ${({
        theme: {
          color: { gray500 },
        },
      }) => gray500};
      direction: rtl;
      font-weight: normal;
      text-align: right;
      cursor: pointer;
      :focus-visible {
        outline: none;
      }

      appearance: none;
      /* for Firefox */
      -moz-appearance: none;
      /* for Chrome */
      -webkit-appearance: none;
    }
  }
`

export default ProductFilterTopBarForV4
